import React, { useEffect, useRef, useState } from "react";
import { WizardEvidenceItem } from "../types";
import {
  isValidYouTubeUrl,
  isValidUrl,
} from "../../../services/evidenceApi";

interface Props {
  items: WizardEvidenceItem[];
  isAnalyzing: boolean;
  analyzeProgress: number | null;
  onAddFiles: (files: File[]) => void;
  onAddUrl: (url: string) => string | null; // returns error message or null
  onRemove: (id: string) => void;
  onAnalyze: () => void;
}

const statusLabel: Record<WizardEvidenceItem["status"], string> = {
  pending: "Ready to analyze",
  analyzing: "Analyzing...",
  done: "Analyzed",
  failed: "Analysis failed",
};

const kindLabel = (item: WizardEvidenceItem): string => {
  if (item.kind === "file") return item.file?.name || "File";
  if (item.kind === "youtube") return item.url || "YouTube video";
  return item.url || "Web page";
};

const kindTag = (item: WizardEvidenceItem): string => {
  if (item.kind === "file") {
    const type = item.file?.type || "";
    if (type.startsWith("image/")) return "Image";
    if (type.startsWith("video/")) return "Video";
    if (type === "application/pdf") return "PDF";
    return "File";
  }
  return item.kind === "youtube" ? "YouTube" : "Link";
};

const EvidenceStep: React.FC<Props> = ({
  items,
  isAnalyzing,
  analyzeProgress,
  onAddFiles,
  onAddUrl,
  onRemove,
  onAnalyze,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlDraft, setUrlDraft] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isAnalyzing) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const timer = setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000
    );
    return () => clearInterval(timer);
  }, [isAnalyzing]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) {
      onAddFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleAddUrl = () => {
    const url = urlDraft.trim();
    if (!url) return;
    if (!isValidYouTubeUrl(url) && !isValidUrl(url)) {
      setUrlError("This doesn't look like a valid link.");
      return;
    }
    const error = onAddUrl(url);
    if (error) {
      setUrlError(error);
    } else {
      setUrlDraft("");
      setUrlError(null);
    }
  };

  const pendingCount = items.filter((i) => i.status === "pending").length;

  return (
    <div className="rw-step">
      <h2 className="rw-step-title">Attach evidence</h2>
      <p className="rw-step-subtitle">
        Optional. Add files or links that support an outcome - screenshots,
        documents, videos, news articles, YouTube clips.
      </p>

      <div
        className={`rw-dropzone ${isDragOver ? "rw-dropzone-active" : ""}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <div className="rw-dropzone-title">Drop files here</div>
        <div className="rw-dropzone-hint">
          or click to browse &middot; images, PDFs, videos, documents
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) {
              onAddFiles(Array.from(e.target.files));
              e.target.value = "";
            }
          }}
        />
      </div>

      <div className="rw-url-row">
        <input
          className={`rw-input ${urlError ? "rw-input-error" : ""}`}
          placeholder="Paste a YouTube or article link..."
          value={urlDraft}
          onChange={(e) => {
            setUrlDraft(e.target.value);
            setUrlError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddUrl();
            }
          }}
        />
        <button
          type="button"
          className="rw-secondary-btn"
          onClick={handleAddUrl}
        >
          Add link
        </button>
      </div>
      {urlError && <div className="rw-error">{urlError}</div>}

      {isAnalyzing && (
        <div className="rw-analysis-panel">
          <div className="rw-analysis-header">
            <span className="rw-spinner rw-spinner-sm" />
            <div>
              <div className="rw-analysis-title">
                {analyzeProgress !== null
                  ? `Uploading evidence... ${analyzeProgress}%`
                  : "The AI is examining your evidence"}
              </div>
              <div className="rw-analysis-sub">
                Images take seconds; videos and long documents can take a
                minute or two. Keep this page open.
                {elapsed > 2 ? ` · ${elapsed}s` : ""}
              </div>
            </div>
          </div>
          <div className="rw-progress-track">
            <div
              className={`rw-progress-fill ${
                analyzeProgress === null ? "rw-progress-indeterminate" : ""
              }`}
              style={
                analyzeProgress !== null
                  ? { width: `${analyzeProgress}%` }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ul className="rw-evidence-list">
          {items.map((item) => (
            <li key={item.id} className={`rw-evidence-item rw-ev-${item.status}`}>
              <span className="rw-evidence-tag">{kindTag(item)}</span>
              <span className="rw-evidence-name" title={kindLabel(item)}>
                {kindLabel(item)}
              </span>
              <span className={`rw-evidence-status rw-status-${item.status}`}>
                {item.status === "analyzing" && (
                  <span className="rw-spinner rw-spinner-xs" />
                )}
                {statusLabel[item.status]}
                {item.status === "failed" && item.error ? ` - ${item.error}` : ""}
              </span>
              {item.status !== "analyzing" && (
                <button
                  type="button"
                  className="rw-icon-btn"
                  aria-label="Remove evidence"
                  onClick={() => onRemove(item.id)}
                >
                  &times;
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {pendingCount > 0 && (
        <button
          type="button"
          className="rw-secondary-btn rw-analyze-btn"
          disabled={isAnalyzing}
          onClick={onAnalyze}
        >
          {isAnalyzing
            ? analyzeProgress !== null
              ? `Uploading... ${analyzeProgress}%`
              : "Analyzing evidence..."
            : `Analyze ${pendingCount} item${pendingCount > 1 ? "s" : ""}`}
        </button>
      )}
      {items.length > 0 && pendingCount === 0 && !isAnalyzing && (
        <p className="rw-hint">
          Failed items will not be sent to the judge. You can remove them or
          continue without them.
        </p>
      )}
    </div>
  );
};

export default EvidenceStep;
